import { Injectable } from '@angular/core';
import { IndexedDBService, Locale, LocaleSetting, localeSettingKeys } from '@rct/lynx-lobbies-shared-lib';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { concatMap, map, mergeMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LynxLocaleSettingService {
  private _localeSettingSubject$: BehaviorSubject<LocaleSetting> =
    new BehaviorSubject<LocaleSetting>(undefined);

  private _localeSetting$ = this._localeSettingSubject$.asObservable();

  constructor(private _indexedDbService: IndexedDBService) {
    this._loadLocaleSetting$().pipe(take(1)).subscribe();
  }

  private _loadLocaleSetting$(): Observable<LocaleSetting> {
    return this._indexedDbService.getAllItems$().pipe(
      map((items) => {
        const localeSetting = items
          .filter((item) =>
            localeSettingKeys.filter((key) => Object.keys(item).includes(key))
          )
          .reduce((ls, item) => {
            const key = localeSettingKeys.find((k) =>
              Object.keys(item).includes(k)
            );
            ls = {
              [key]: item[key],
              ...ls,
            };
            return ls;
          }, {});

        this._localeSettingSubject$.next(localeSetting);

        return localeSetting;
      })
    );
  }

  getLocaleSetting$(): Observable<LocaleSetting> {
    return this._localeSetting$;
  }

  updateLocaleSetting$(
    localeSetting: Partial<LocaleSetting>
  ): Observable<LocaleSetting> {
    return this._indexedDbService.getAllItems$().pipe(
      map((items) => this._updateDbLocaleSettings(localeSetting, items)),
      concatMap((items) =>
        combineLatest(
          items.map((item) => this._indexedDbService.updateItem$(item))
        )
      ),
      mergeMap(() => this._loadLocaleSetting$())
    );
  }

  convertToLocale(locale: string): Locale {
    return locale.replace(
      /(\w+)-(\w+)/,
      (match, p1, p2) => `${p1.toLowerCase()}-${p2.toUpperCase()}`
    ) as Locale;
  }

  private _updateDbLocaleSettings(
    localeSetting: Partial<LocaleSetting>,
    dbItems: { [key: string]: any }[]
  ): { [key: string]: any }[] {
    return dbItems.map((item) => {
      return Object.keys(item).reduce(
        (updatedItem, key) => {
          if (localeSetting.hasOwnProperty(key)) {
            return { ...updatedItem, [key]: localeSetting[key] };
          }
          return updatedItem;
        },
        { ...item }
      );
    });
  }
}
